import React, { useCallback, useEffect, useRef, useState } from "react";
import CardTable from "../../../components/AdminTable/CardTable";
import axios from "axios";
import { apiKey } from "../../../config/config";
import handleCSRF from "../../../config/CSRF";
import InvoiceMain from "../../../components/Invoices/InvoiceMain";
import { FaCog, FaEye } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { languageHelper } from "../../../config/helperFunctions";

const defaultLang = localStorage.getItem("rms-lang") || "text_en";

function AllSales() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(false);
  const columns = [
    {
      header: languageHelper(defaultLang, "SN") || "SN",
      cell: (row) => row.row.index + 1,
    },
    {
      header: languageHelper(defaultLang, "Refrence No") || "Refrence No",
      accessorKey: "sale_no",
    },
    {
      header: languageHelper(defaultLang, "Order Type") || "Order Type",
      accessorKey: "order_type",
      cell: (row) => row.getValue()?.name,
    },
    {
      header: languageHelper(defaultLang, "Date(Time)") || "Date(Time)",
      accessorKey: "date_time",
    },
    {
      header: languageHelper(defaultLang, "Customer") || "Customer",
      accessorKey: "customer",
      cell: (row) => row.getValue()?.name || row.row.original.guest.name,
    },
    {
      header: languageHelper(defaultLang, "Order By") || "Order By",
      accessorKey: "accepted_from",
      cell: (row) => row.getValue(),
    },
    {
      header: languageHelper(defaultLang, "Total Payable") || "Total Payable",
      accessorKey: "total_payable",
    },
    {
      header: languageHelper(defaultLang, "Payment Method") || "Payment Method",
      accessorKey: "payment",
      cell: (row) => row.getValue()?.name,
    },

    {
      header: languageHelper(defaultLang, "Added By") || "Added By",
      accessorKey: "AddedBy",
    },
    {
      header: languageHelper(defaultLang, "Actions") || "Actions",
      cell: (row) => (
        <ActionDropdown
          getInvoiceData={getInvoiceData}
          id={row.row.original.id}
        />
      ),
    },
  ];
  const [data, setData] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [setting, setSetting] = useState();
  const [buttonData] = useState([
    {
      buttonText: "Add Sale ",
      path: "/POS/main",
    },
  ]);
  const [printQueue, setPrintQueue] = useState([]);
  const printedRefs = useRef(new Map());

  const componentRef = useRef();

  const getInvoiceData = (id) => {
    resetPrintedRefs();
    setInvoiceData(null);
    axios
      .get(`${apiKey}/api/rms/customer/fetch_order/${id}`, {
        params: {
          orderId: id,
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        setInvoiceData(res.data);
      })
      .catch((err) => {
        setError(err.response.data.message);
      });
  };

  const resetPrintedRefs = () => {
    printedRefs.current.clear();
  };

  // const handlePrint = useReactToPrint({
  //   content: () => componentRef.current,
  //   documentTitle: "Invoice",
  // });
  const fallbackToReactPrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Invoice",
  });

  const handlePrint = useCallback(
    async (printerName = "Default", componentRef) => {
      if (
        window.JSPM.JSPrintManager.websocket_status !==
        window.JSPM.WSStatus.Open
      ) {
        console.error("WebSocket not ready, falling back to react-to-print");
        fallbackToReactPrint();
        return;
      }
      if (printedRefs.current.has(printerName)) return;

      printedRefs.current.set(printerName, true); // Mark as printed

      try {
        throw new Error("Simulated error for testing fallback"); // Simulate an error for testing fallback
        // eslint-disable-next-line no-unreachable
        const canvas = await html2canvas(componentRef.current, {
          useCORS: true,
        });
        const base64image = canvas.toDataURL("image/png").split(",")[1];

        const cpj = new window.JSPM.ClientPrintJob();
        cpj.clientPrinter =
          printerName === "Default" ||
          !printerName ||
          printerName === "null" ||
          printerName === "Default Invoice"
            ? new window.JSPM.DefaultPrinter()
            : new window.JSPM.InstalledPrinter(printerName);

        cpj.files.push(
          new window.JSPM.PrintFile(
            base64image,
            window.JSPM.FileSourceType.Base64,
            "invoice.png",
            1
          )
        );

        await cpj.sendToClient();
        console.log(`Print job sent to ${printerName}`);
      } catch (error) {
        console.error("Error during silent print:", error);
        fallbackToReactPrint();
      }
    },
    [fallbackToReactPrint]
  );

  useEffect(() => {
    if (printQueue.length === 0) return;
    const nextPrinter = printQueue[0];

    handlePrint(nextPrinter).finally(() => {
      setPrintQueue((prev) => prev.slice(1)); // Remove the first printer
    });
  }, [handlePrint, printQueue]);

  // useEffect(() => {
  //   if (window.JSPM && window.JSPM.JSPrintManager) {
  //     window.JSPM.JSPrintManager.auto_reconnect = true;
  //     window.JSPM.JSPrintManager.start();
  //   } else {
  //     console.error("JSPrintManager not loaded.");
  //   }
  // }, []);

  useEffect(() => {
    const fetchOrders = () => {
      axios
        .get(`${apiKey}/api/rms/customer/fetch_all_order_light`, {
          params: {
            orderFrom: "pos",
            outletId: user?.user.outlet_id,
          },
        })
        .then((res) => {
          setData(res.data);
        })
        .catch((err) => {
          setError(err.response.data.message);
        })
        .finally(() => {
          setPageLoading(false);
        });
    };
    handleCSRF(fetchOrders);

    axios
      .get(`${apiKey}/api/rms/admin/settings`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setSetting(res.data);
      })
      .catch((err) => {
        setError(err.response.data.message);
      });
  }, [user?.token, user?.user.outlet_id]);

  return (
    <main>
      <CardTable
        data={data}
        columns={columns}
        heading={languageHelper(defaultLang, "POS Orders")}
        buttonData={buttonData}
        error={error}
      />
      {pageLoading && (
        <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
          <img src="/assets/gif/dash2.gif" alt="" />
        </div>
      )}
      {/* {invoiceData && (
        <div className="hidden">
          <div ref={componentRef}>
            <InvoiceMain setting={setting} invoiceDetails={invoiceData} />
          </div>
        </div>
      )} */}
      {invoiceData && (
        <div
          // onLoad={fallbackToReactPrint}
          className="absolute left-[-9999px] top-0 opacity-0"
        >
          <div ref={componentRef}>
            <InvoiceMain
              setting={setting}
              invoiceDetails={invoiceData}
              handlePrint={handlePrint}
            />
          </div>
        </div>
      )}
    </main>
  );
}

function ActionDropdown({ getInvoiceData, id }) {
  const [showDropdown, setShowDropDown] = useState(false);

  return (
    <>
      <div className="relative">
        <button
          className="text-blueGray-500 px-3 flex items-center justify-between gap-1 border rounded-full"
          onClick={() => setShowDropDown(!showDropdown)}
        >
          <FaCog className="text-d-bg-text" />
          <IoIosArrowDown className="text-d-bg-text" />
        </button>
        <div
          className={
            (showDropdown ? "block " : "hidden ") +
            "absolute top-4 left-0 -translate-x-[75%] bg-white text-base z-10 float-left py-2 list-none text-left rounded shadow-lg min-w-40"
          }
        >
          <button
            className={
              "text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap bg-transparent text-blueGray-700"
            }
            onClick={() => getInvoiceData(id)}
          >
            <FaEye />
            <p> {languageHelper(defaultLang, "View Invoice")} </p>
          </button>
        </div>
      </div>
    </>
  );
}

export default AllSales;
